import React, { useEffect, useState } from "react";
import Form from "../../Form";
import { Drawer } from "antd";
import styles from "./TicketFilterDrawer.module.scss";
import {
  filterFieldConstants,
  filterFormMap,
  filterHeaderStyle,
  getFilterOptionFields,
  ticketFilterConstants,
} from "./constants";
import TNButton from "../../../reusable-components/TNButton";
import { ButtonShape, ButtonSize } from "../../../enums/ButtonAttributesEnums";
import InputField from "../../../reusable-components/TNInputField";
import TicketUploadServices from "../../../services/ticketUpload.service";
import { TicketUploadFilterProps } from "../../../Types";
import moment from "moment";
import { getFilteredOptions } from "../../../utils/getDropdownOptions";
import { InputfieldTypes } from "../../../enums/InputFieldAttributesEnums";
import { useFormContext } from "react-hook-form";

export interface TicketViewFilterProps {
  isOpen?: boolean;
  handleClose?: () => void;
  handleApplyFilter?: (items: TicketUploadFilterProps) => void;
  handleClearFilter?: () => void;
  filterValues?: TicketUploadFilterProps;
  clearAllButton?: boolean;
  handleDisableClearAllButton?: () => void;
}

const TicketViewFilter = (props: TicketViewFilterProps) => {
  const {
    isOpen,
    handleClose,
    handleApplyFilter,
    handleClearFilter,
    filterValues,
    clearAllButton,
    handleDisableClearAllButton,
  } = props;

  const { setValue, resetField, getValues } = useFormContext();
  const { FetchTicketUploadFilterMeta } = TicketUploadServices();
  const { data, refetch: refetchTicketUplaodMeta } =
    FetchTicketUploadFilterMeta();

  const customerOptions = data?.customers?.map((item) => {
    return {
      label: item?.companyName,
      value: String(item?.companyId),
    };
  });

  const truckOptions = data?.trucks?.map((item) => {
    return {
      label: item?.truckId,
      value: String(item?.truckId),
    };
  });

  const orderOptions = data?.orders?.map((item) => {
    return {
      label: item?.materialName,
      value: String(item?.orderId),
    };
  });

  const jobIdOptions = data?.jobNumbers?.map((item) => {
    return {
      label: item?.jobNumber,
      value: String(item?.jobNumber),
    };
  });

  const jobNameOptions = data?.jobNames?.map((item) => {
    return {
      label: item?.jobName,
      value: String(item?.jobName),
    };
  });

  const [customerSearchValue, setCustomerSearchValue] = useState("");
  const [jobIdSearchValue, setJobIdSearchValue] = useState("");
  const [orderSearchValue, setOrderSearchValue] = useState("");
  const [truckSearchValue, setTruckSearchValue] = useState("");
  const [jobNameSearchValue, setJobNameSearchValue] = useState("");

  const FilterOptionFields = getFilterOptionFields({
    customerSearchValue,
    jobIdSearchValue,
    orderSearchValue,
    truckSearchValue,
    jobNameSearchValue,
    setCustomerSearchValue,
    setJobIdSearchValue,
    setOrderSearchValue,
    setTruckSearchValue,
    setJobNameSearchValue,
  });

  const getOptionsList = (fieldName: string) => {
    switch (fieldName) {
      case filterFieldConstants.CUSTOMER_FILTER_NAME:
        return getFilteredOptions(customerOptions, customerSearchValue) ?? [];
      case filterFieldConstants.JOB_ID_FILTER_NAME:
        return getFilteredOptions(jobIdOptions ?? [], jobIdSearchValue) ?? [];
      case filterFieldConstants.ORDER_FILTER_NAME:
        return getFilteredOptions(orderOptions ?? [], orderSearchValue) ?? [];
      case filterFieldConstants.TRUCK_FILTER_NAME:
        return getFilteredOptions(truckOptions ?? [], truckSearchValue) ?? [];
      case filterFieldConstants.JOB_NAME_FILTER_NAME:
        return (
          getFilteredOptions(jobNameOptions ?? [], jobNameSearchValue) ?? []
        );
    }
  };

  const [values, setValues] = useState({});

  useEffect(() => {
    if (!clearAllButton) return;
    setValues({});
    clearFormFields();
    handleDisableClearAllButton();
  }, [clearAllButton]);

  const DrawerHeader = (
    <div className={styles.DrawerHeaderContainer}>
      <p>{ticketFilterConstants.HEADER_TXT}</p>
    </div>
  );

  const clearFormFields = () => {
    resetField(filterFieldConstants.DATE_FILTER_NAME);
    setValues({});
  };

  const handleFilterPersistance = () => {
    filterFormMap.forEach(({ key, field }, index) => {
      const filterValue = filterValues[key];
      const formValue = values[field];
      if (!!filterValue && !!!formValue) {
        setValues((prev) => ({
          ...prev,
          [field]: filterValue,
        }));
      } else if (!!!filterValue && !!formValue) {
        setValues((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      } else if (!!filterValue && !!formValue && filterValue !== formValue) {
        setValues((prev) => ({
          ...prev,
          [field]: filterValue,
        }));
      }
    });

    const dateFieldValue = getValues(filterFieldConstants.DATE_FILTER_NAME);
    const fromDateValue = filterValues?.fromDate;
    const toDateValue = filterValues?.toDate;

    if (!!fromDateValue && !!!dateFieldValue)
      setValue(filterFieldConstants.DATE_FILTER_NAME, [
        moment(fromDateValue),
        moment(toDateValue),
      ]);
    else if (!!!fromDateValue && !!dateFieldValue)
      setValue(filterFieldConstants.DATE_FILTER_NAME, [undefined, undefined]);
    else if (
      !!fromDateValue &&
      !!dateFieldValue &&
      fromDateValue !== dateFieldValue[0] &&
      toDateValue !== dateFieldValue[1]
    )
      setValue(filterFieldConstants.DATE_FILTER_NAME, [
        moment(fromDateValue),
        moment(toDateValue),
      ]);
  };

  const handleFilterCloseAction = () => {
    handleFilterPersistance();
    handleClose();
  };

  const DrawerFooter = (
    <div className={styles.DrawerFooterContainer}>
      <TNButton
        className={ticketFilterConstants.CANCEL_BTN_CLASS}
        shape={ButtonShape.DEFAULT}
        size={ButtonSize.LARGE}
        text={ticketFilterConstants.CANCEL_BTN_TXT}
        onClick={handleFilterCloseAction}
      />
      <TNButton
        shape={ButtonShape.DEFAULT}
        size={ButtonSize.LARGE}
        text={ticketFilterConstants.SUBMIT_BTN_TXT}
        onClick={() => {
          handleApplyFilter({
            customerNames: values[filterFieldConstants.CUSTOMER_FILTER_NAME],
            truckNumbers: values[filterFieldConstants.TRUCK_FILTER_NAME],
            orderIds: values[filterFieldConstants.ORDER_FILTER_NAME],
            jobNumbers: values[filterFieldConstants.JOB_ID_FILTER_NAME],
            jobNames: values[filterFieldConstants.JOB_NAME_FILTER_NAME],
            fromDate: getValues(filterFieldConstants.DATE_FILTER_NAME)?.[0],
            toDate: getValues(filterFieldConstants.DATE_FILTER_NAME)?.[1],
          });
        }}
      />
    </div>
  );

  return (
    <Drawer
      visible={isOpen}
      maskClosable={false}
      destroyOnClose
      mask={true}
      closable={true}
      getContainer={false}
      placement={ticketFilterConstants.DRAWER_PLACEMENT}
      size={ticketFilterConstants.DRAWER_SIZE}
      title={DrawerHeader}
      footer={DrawerFooter}
      headerStyle={filterHeaderStyle}
      className={styles.ticketViewFilters}
      height={ticketFilterConstants.DRAWER_HEIGHT}
      width={ticketFilterConstants.DRAWER_WIDTH}
      onClose={handleFilterCloseAction}
    >
      <>
        <div className={styles.clearFilterWrapper}>
          <span
            className={styles.clearTextContainer}
            onClick={() => {
              handleClearFilter();
              clearFormFields();
            }}
          >
            {ticketFilterConstants.CLEAR_BTN_TXT}
          </span>
        </div>
        <div className={styles.filterFieldsWrapper}>
          {FilterOptionFields.map((field) => {
            const value = values[field.name] ?? [];
            const handleChange = (newValue) => {
              setValues((prev) => ({
                ...prev,
                [field.name]: newValue,
              }));
            };
            return (
              <InputField
                type={field.type}
                name={field.name}
                label={field.label}
                bordered={field.bordered}
                placeholder={field.placeholder}
                showSearch={field.showSearch}
                optionFilterProp={ticketFilterConstants.SEARCH_FILTER_PROP}
                disabledDates={field.disabledDates}
                options={getOptionsList(field.name)}
                values={value}
                onChange={handleChange}
                resetCheckbox={() => handleChange([])}
                allowDateClear={true}
                setSearch={field.setSearch}
                dropdownClassName={styles.customDropdown}
                searchValue={field.searchValue}
              />
            );
          })}
        </div>
      </>
    </Drawer>
  );
};

export default TicketViewFilter;
